/**
 * Workspaces — the top-level axis that scopes navigation, mirroring HMS's
 * "sections" (`src/constants/sections.js`).
 *
 * ADR-0011 removed the sidebar in favour of a horizontal header menu, which
 * only stays readable if it lists one workspace's modules rather than every
 * module in the ERP. A user works in Manufacturing *or* Finance; the menu
 * follows.
 *
 * This module holds the constants plus small helpers that operate on an
 * already permission-filtered `{ [workspace]: navItems }` map, so it stays
 * independent of the nav config itself.
 */

export const WORKSPACES = {
  manufacturing: 'manufacturing',
  finance: 'finance',
  /**
   * Access control and, later, system settings. Every module in it carries an
   * admin `permission`, so a user holding neither `user.manage` nor
   * `role.manage` has zero items here and `getAccessibleWorkspaces` omits the
   * workspace entirely — the switcher never offers it (ADR-0012, design.md
   * §5.3). That is why the Dashboard is deliberately **not** listed in it.
   */
  administration: 'administration',
} as const;

export type Workspace = (typeof WORKSPACES)[keyof typeof WORKSPACES];

export const WORKSPACE_LABELS: Record<Workspace, string> = {
  [WORKSPACES.manufacturing]: 'Manufacturing',
  [WORKSPACES.finance]: 'Finance',
  [WORKSPACES.administration]: 'Administration',
};

/**
 * One line of orientation per workspace, shown in the workspace picker modal.
 * Phrased as the organising principle from ADR-0012 §2 — physical goods vs
 * money vs who-may-do-what — so the choice reads as a scope, not a menu.
 *
 * Presentation only; nothing branches on this.
 */
export const WORKSPACE_DESCRIPTIONS: Record<Workspace, string> = {
  [WORKSPACES.manufacturing]: 'Production, stock and the physical movement of goods.',
  [WORKSPACES.finance]: 'Invoices, payments and the movement of money.',
  [WORKSPACES.administration]: 'Users, roles and how the system behaves.',
};

/**
 * Priority order for picking a default landing workspace: the first one here
 * the signed-in user has at least one permitted module in.
 *
 * Administration is last on purpose — an administrator who also works in
 * Manufacturing should land on the plant, not on the user list.
 */
export const WORKSPACE_ORDER: readonly Workspace[] = [
  WORKSPACES.manufacturing,
  WORKSPACES.finance,
  WORKSPACES.administration,
];

/** A `{ [workspace]: navItems }` map, already permission-filtered. */
export type WorkspaceNavMap<TItem> = Record<Workspace, readonly TItem[]>;

/**
 * Builds a complete workspace map. `WORKSPACE_ORDER` must list every
 * workspace, which is what makes the seeded accumulator safe — every key is
 * assigned before the map is returned.
 */
export function buildWorkspaceMap<TItem>(
  build: (workspace: Workspace) => TItem[],
): WorkspaceNavMap<TItem> {
  const map = {} as Record<Workspace, TItem[]>;
  for (const workspace of WORKSPACE_ORDER) {
    map[workspace] = build(workspace);
  }
  return map;
}

export function isWorkspace(value: unknown): value is Workspace {
  return typeof value === 'string' && Object.values(WORKSPACES).includes(value as Workspace);
}

/**
 * The active workspace lives in the **query string**, not the path.
 *
 * Rationale (an improvement on HMS, whose switcher does not touch the URL):
 *  - ADR-0008 already puts drawer state in the query string (`?record=123`).
 *    Workspace is the same class of state — a view scope, not a resource
 *    identity — so it uses the same mechanism instead of a second one, and
 *    the two compose: `/purchase-orders?workspace=finance&record=123`.
 *  - A path segment would bake the workspace into every module's canonical
 *    URL, forcing a decision about which workspace "owns" each module. That
 *    mapping is the architect's to make, and a module may legitimately appear
 *    in both (the dashboard already does).
 *  - Reload and Back both preserve it either way; switching pushes a history
 *    entry so Back returns to the previous workspace.
 * Moving to path segments later stays possible behind a redirect.
 *
 * It lives here rather than in `use-workspace.ts` because `nav-items.tsx`
 * needs `workspaceSearch` below, and `use-workspace.ts` imports `nav-items`.
 */
export const WORKSPACE_QUERY_PARAM = 'workspace';

/**
 * Projects a location's query string down to **the workspace key alone** —
 * the only parameter a cross-module link may carry.
 *
 * Every navigation that leaves the current module must preserve `?workspace=`
 * (ADR-0012), or the header menu silently switches workspace under the user.
 * Carrying the *whole* `search` to do it is over-broad, and the surplus is not
 * inert: `search` also holds `record`, `mode`, `page` and the list filters, so
 * `/users?workspace=administration&record=<user-uuid>&mode=view` → Roles
 * arrived as `/roles?record=<user-uuid>&mode=view`, where `useRecordParam`
 * opened a role drawer for a *user* id and showed "not found". Those keys
 * identify a record within one module and are meaningless in the next.
 *
 * Returns `''` (not `'?'`) when there is no valid workspace to carry, so the
 * link renders a clean URL. A malformed value is dropped rather than
 * propagated — `useWorkspace` would only normalise it away on arrival.
 */
export function workspaceSearch(search: string): string {
  const value = new URLSearchParams(search).get(WORKSPACE_QUERY_PARAM);
  if (!isWorkspace(value)) return '';
  return `?${new URLSearchParams({ [WORKSPACE_QUERY_PARAM]: value }).toString()}`;
}

/**
 * The workspaces that have at least one visible module for this user — i.e.
 * the segments `WorkspaceSwitcher` should offer.
 */
export function getAccessibleWorkspaces<TItem>(
  navByWorkspace: WorkspaceNavMap<TItem>,
): Workspace[] {
  return WORKSPACE_ORDER.filter((workspace) => (navByWorkspace[workspace] ?? []).length > 0);
}

/**
 * First accessible workspace in `WORKSPACE_ORDER`, or `null` when the user has
 * no permitted module anywhere (guards against an empty menu).
 */
export function getDefaultWorkspace<TItem>(
  navByWorkspace: WorkspaceNavMap<TItem>,
): Workspace | null {
  return getAccessibleWorkspaces(navByWorkspace)[0] ?? null;
}

/**
 * The workspace that should actually be active: the explicit selection if it
 * is still accessible, otherwise the default.
 *
 * This fallback is what stops an empty menu — on first load the selection is
 * null, and after a role change a previously selected workspace may no longer
 * be permitted.
 */
export function resolveActiveWorkspace<TItem>(
  selected: Workspace | null | undefined,
  navByWorkspace: WorkspaceNavMap<TItem>,
): Workspace | null {
  if (selected && (navByWorkspace[selected] ?? []).length > 0) {
    return selected;
  }
  return getDefaultWorkspace(navByWorkspace);
}
